import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, inr, fdate } from "@/api";
import { PageHeader, StatCard, EmptyState } from "@/components/Shared";
import { IndianRupee, Receipt, AlertTriangle, ArrowDownCircle, ArrowUpCircle, Landmark, Package, Plus } from "lucide-react";

export default function Dashboard() {
  const [d, setD] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then((r) => setD(r.data)).catch(() => {});
  }, []);

  if (!d)
    return <div className="py-20 text-center font-mono2 text-sm text-emerald-600 animate-pulse" data-testid="dashboard-loading">Loading books…</div>;

  return (
    <div data-testid="portal-dashboard-overview">
      <PageHeader title="Dashboard" sub={`Today: ${inr(d.today_income)} in · ${inr(d.today_expense)} out`}>
        <Link to="/app/invoices/new" data-testid="quick-new-invoice-btn"
          className="inline-flex items-center gap-2 rounded-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 text-sm font-semibold px-4 py-2 transition-colors duration-200">
          <Plus className="w-4 h-4" /> New Invoice
        </Link>
        <Link to="/app/purchases" data-testid="quick-new-purchase-btn"
          className="inline-flex items-center gap-2 rounded-full border border-slate-300 hover:border-emerald-500/50 text-slate-700 hover:text-emerald-700 text-sm font-medium px-4 py-2 transition-colors duration-200">
          <Plus className="w-4 h-4" /> New Purchase
        </Link>
      </PageHeader>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard testid="stat-total-sales" label="Total Sales" value={inr(d.total_sales)} icon={IndianRupee} />
        <StatCard testid="stat-gst-collected" label="GST Collected" value={inr(d.gst_collected)} icon={Receipt} tone="blue" />
        <StatCard testid="stat-receivables" label="Client Receivables" value={inr(d.receivables)} icon={ArrowDownCircle} tone="amber" />
        <StatCard testid="stat-payables" label="Vendor Payables" value={inr(d.payables)} icon={ArrowUpCircle} tone="red" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {d.low_stock.length > 0 && (
            <div className="rounded-2xl border border-red-500/30 bg-red-500/5 p-5" data-testid="low-stock-alerts">
              <div className="flex items-center gap-2 mb-4">
                <AlertTriangle className="w-4 h-4 text-red-600" />
                <h3 className="font-semibold text-red-700">Low Stock Alerts ({d.low_stock.length})</h3>
              </div>
              <div className="space-y-2">
                {d.low_stock.map((p) => (
                  <div key={p.product_id} className="flex items-center justify-between rounded-xl bg-white border border-slate-200 px-4 py-2.5" data-testid={`low-stock-${p.product_id}`}>
                    <span className="text-sm text-slate-800">{p.name}</span>
                    <span className="font-mono2 text-sm text-red-600">{p.stock} {p.unit} left · reorder at {p.reorder_level}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="recent-invoices-card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-slate-900">Recent Tax Invoices</h3>
              <Link to="/app/invoices" className="text-xs text-emerald-600 hover:text-emerald-700" data-testid="view-all-invoices-link">View all</Link>
            </div>
            {d.recent_invoices.length === 0 ? (
              <EmptyState icon={Receipt} title="No invoices yet" sub="Create your first GST tax invoice" />
            ) : (
              <div className="divide-y divide-slate-200">
                {d.recent_invoices.map((i) => (
                  <Link to={`/invoice/${i.invoice_id}/print`} key={i.invoice_id} data-testid={`recent-invoice-${i.invoice_id}`}
                    className="flex items-center justify-between py-3 hover:bg-slate-50 px-2 rounded-lg transition-colors duration-150">
                    <div>
                      <p className="text-sm font-medium text-slate-900 font-mono2">{i.number}</p>
                      <p className="text-xs text-slate-500">{i.client.name} · {fdate(i.date)}</p>
                    </div>
                    <span className="font-mono2 font-bold text-emerald-600">{inr(i.grand_total)}</span>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="space-y-6">
          <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="bank-balances-card">
            <div className="flex items-center gap-2 mb-4">
              <Landmark className="w-4 h-4 text-emerald-600" />
              <h3 className="font-semibold text-slate-900">Bank Balances</h3>
            </div>
            <div className="space-y-3">
              {d.banks.map((b) => (
                <div key={b.account_id} className="flex items-center justify-between rounded-xl bg-slate-100 px-4 py-3" data-testid={`bank-balance-${b.account_id}`}>
                  <span className="text-sm text-slate-700">{b.name}</span>
                  <span className="font-mono2 font-bold text-slate-900">{inr(b.balance)}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="inventory-summary-card">
            <div className="flex items-center gap-2 mb-3">
              <Package className="w-4 h-4 text-emerald-600" />
              <h3 className="font-semibold text-slate-900">Inventory</h3>
            </div>
            <p className="font-mono2 text-2xl font-bold text-slate-900">{d.product_count} <span className="text-xs text-slate-500 font-normal">products</span></p>
            <p className="font-mono2 text-2xl font-bold text-slate-900 mt-2">{d.invoice_count} <span className="text-xs text-slate-500 font-normal">invoices raised</span></p>
          </div>
        </div>
      </div>
    </div>
  );
}
